// src/firebase.js
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  confirmPasswordReset,
  applyActionCode,
} from 'firebase/auth';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { getFirestore } from 'firebase/firestore';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from './constants/Routes';
// Must load before getAnalytics so its automatic page_view never sees a /map query string.
import './utils/inboundMapQuery';

// Build the locale-prefixed /login URL used as the "return to app" target in
// Firebase email action links (verification + password reset). We read the
// user's current language from localStorage (persisted by i18next under
// `bloom-language`) so Spanish users land back on `/es/login`.
const getLocalizedLoginUrl = () => {
  const stored = localStorage.getItem('bloom-language');
  const locale = SUPPORTED_LOCALES.includes(stored) ? stored : DEFAULT_LOCALE;
  return `${window.location.origin}/${locale}/login`;
};

// Firebase configuration for authentication (bloom-dev project)
// Values loaded from environment variables set in .env files or CI/CD
const authFirebaseConfig = {
  apiKey: import.meta.env.VITE_AUTH_API_KEY,
  authDomain: import.meta.env.VITE_AUTH_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_AUTH_PROJECT_ID,
  storageBucket: import.meta.env.VITE_AUTH_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_AUTH_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_AUTH_APP_ID,
  measurementId: import.meta.env.VITE_AUTH_MEASUREMENT_ID
};

// Firebase configuration for analytics (webapp-specific project)
// Values loaded from environment variables set in .env files or CI/CD
const analyticsFirebaseConfig = {
  apiKey: import.meta.env.VITE_ANALYTICS_API_KEY,
  authDomain: import.meta.env.VITE_ANALYTICS_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_ANALYTICS_PROJECT_ID,
  storageBucket: import.meta.env.VITE_ANALYTICS_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_ANALYTICS_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_ANALYTICS_APP_ID,
  measurementId: import.meta.env.VITE_ANALYTICS_MEASUREMENT_ID
}

// Initialize Firebase for authentication with a unique name ('auth')
const authApp = initializeApp(authFirebaseConfig, 'auth');
const auth = getAuth(authApp);

// Initialize Firestore for user preferences storage
const db = getFirestore(authApp);

// Initialize Firebase for analytics with a unique name ('analytics')
const analyticsApp = initializeApp(analyticsFirebaseConfig, 'analytics');
const analytics = getAnalytics(analyticsApp);

// Note: Firebase Analytics automatically tracks first_open and session_start events
// We don't need to manually log app_start/session_start as they would be attributed
// to an anonymous user before authentication completes

// OAuth Providers
const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider('apple.com');

// Configure providers
googleProvider.setCustomParameters({
  prompt: 'select_account',
});

// Apple returns nothing beyond the user identifier unless the scopes are asked
// for explicitly — GoogleAuthProvider requests profile/email by default, but
// OAuthProvider does not. Without `name`, `user.displayName` is null on every
// Apple sign-in, so social signup registered the account with a blank name.
// Apple only sends the name on the user's FIRST authorization for the app; it
// is never resent on later sign-ins.
appleProvider.addScope('email');
appleProvider.addScope('name');

// OAuth Sign-In Functions
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (error) {
    console.error('Google sign-in error:', error);
    throw error;
  }
};

export const signInWithApple = async () => {
  try {
    const result = await signInWithPopup(auth, appleProvider);
    return result.user;
  } catch (error) {
    console.error('Apple sign-in error:', error);
    throw error;
  }
};

// Email Verification Functions
export const sendEmailVerificationLink = async (user) => {
  try {
    await sendEmailVerification(user, {
      url: getLocalizedLoginUrl(),
      handleCodeInApp: false,
    });
    return true;
  } catch (error) {
    console.error('Error sending verification email:', error);
    throw error;
  }
};

export const verifyEmailWithCode = async (code) => {
  try {
    await applyActionCode(auth, code);
    return true;
  } catch (error) {
    console.error('Error verifying email:', error);
    throw error;
  }
};

// Password Reset Functions
export const sendPasswordReset = async (email) => {
  try {
    await firebaseSendPasswordResetEmail(auth, email, {
      url: getLocalizedLoginUrl(),
      handleCodeInApp: false,
    });
    logEvent(analytics, 'password_reset_request');
    return true;
  } catch (error) {
    console.error('Error sending password reset:', error);
    throw error;
  }
};

export const confirmPasswordResetWithCode = async (code, newPassword) => {
  try {
    await confirmPasswordReset(auth, code, newPassword);
    logEvent(analytics, 'password_reset_success');
    return true;
  } catch (error) {
    console.error('Error confirming password reset:', error);
    throw error;
  }
};

// Export the initialized services
export { authApp, auth, db, analyticsApp, analytics, logEvent };